import { Role } from 'API';
import { useUserCompanies, useUserHasRole } from 'app/shared-components/auth/AuthProvider';
import { useUserRole } from 'app/hooks/useUserRole';
import { Navigate, useLocation } from 'react-router-dom';

export interface RequireUserRoleProps {
  role: Role;
  LoadingComponent?: JSX.Element;
  FallbackComponent?: JSX.Element;
  children: React.ReactNode;
}

function RequireUserRole({
  role,
  LoadingComponent,
  FallbackComponent,
  children,
}: RequireUserRoleProps): JSX.Element {
  const location = useLocation();
  const userHasRole = useUserHasRole(role);

  // Define exception paths that should bypass the role check
  const exceptionPaths = ['/settings/connectivity'];

  // Check if the current path starts with an exception
  const isException = exceptionPaths.some((path) => location.pathname.startsWith(path));

  if (isException) {
    return <>{children}</>; // Allow access without role check
  }

  if (LoadingComponent && userHasRole === undefined) {
    return LoadingComponent;
  }

  if (FallbackComponent && !userHasRole) {
    return FallbackComponent;
  }

  if (userHasRole) {
    return <>{children}</>;
  }

  return <></>;
}

export type RequireAdministratorProps = Omit<RequireUserRoleProps, 'role'>;

export function RequireAdministrator({
  LoadingComponent,
  FallbackComponent,
  children,
}: RequireAdministratorProps): JSX.Element {
  // SEE-946: route the Administrator gate through useUserRole so the
  // fail-open path (Secondary/Approval roles get treated as ADMINISTRATOR
  // when the per-paidol flag is off) is honored. The legacy code path
  // (useUserHasRole) does a raw `userRole === ADMINISTRATOR` comparison
  // that would render a blank page for fail-open users.
  const location = useLocation();
  const { isAdministrator } = useUserRole();
  const { userRole, isUserCompaniesStateUnknown, selectedPaidolId } = useUserCompanies();

  const exceptionPaths = ['/settings/connectivity'];
  const isException = exceptionPaths.some((path) => location.pathname.startsWith(path));

  if (isException) {
    return <>{children}</>;
  }
  // Preserve the pre-SEE-946 loading semantic of useUserHasRole, which
  // returned `undefined` while userCompanies was still loading. If a caller
  // passed LoadingComponent, render it instead of the Fallback during load.
  // Missing userRole counts as loading only until a company is selected: with
  // a selection in place, no role is a RESOLVED state (a non-support user with
  // no membership row fails closed) and must reach the Fallback, not spin.
  if (LoadingComponent && (isUserCompaniesStateUnknown || (!userRole && !selectedPaidolId))) {
    return LoadingComponent;
  }
  if (isAdministrator) {
    return <>{children}</>;
  }
  if (FallbackComponent) {
    return FallbackComponent;
  }
  return <></>;
}

export function RequireGroupAdministrator({
  LoadingComponent,
  FallbackComponent,
  children,
}: RequireAdministratorProps): JSX.Element {
  // SEE-946: route through useUserRole so the fail-open path (Secondary/
  // Approval treated as Administrator when the per-paidol flag is off) is
  // honored, and Secondary admins (matrix: manage cards/users) pass the
  // Group-Admin-tier gate alongside the existing Primary + Group Admin.
  // Pre-SEE-946 the legacy useUserHasRole(GROUP_ADMINISTRATOR) returned
  // true for ADMINISTRATOR or GROUP_ADMINISTRATOR; the new check returns
  // true for that set plus Secondary (matrix) and fail-open users.
  const location = useLocation();
  const { isAdministrator, isSecondaryAdmin, isGroupAdmin } = useUserRole();
  const { userRole, isUserCompaniesStateUnknown, selectedPaidolId } = useUserCompanies();
  const hasAccess = isAdministrator || isSecondaryAdmin || isGroupAdmin;

  const exceptionPaths = ['/settings/connectivity'];
  const isException = exceptionPaths.some((path) => location.pathname.startsWith(path));

  if (isException) {
    return <>{children}</>;
  }
  // Preserve the pre-SEE-946 loading semantic (LoadingComponent renders
  // while userCompanies is still loading). Missing userRole counts as loading
  // only until a company is selected: with a selection in place, no role is a
  // RESOLVED state (a non-support user with no membership row fails closed)
  // and must reach the Fallback, not spin.
  if (LoadingComponent && (isUserCompaniesStateUnknown || (!userRole && !selectedPaidolId))) {
    return LoadingComponent;
  }
  if (hasAccess) {
    return <>{children}</>;
  }
  if (FallbackComponent) {
    return FallbackComponent;
  }
  return <></>;
}

export function RequireMember(props: RequireAdministratorProps): JSX.Element {
  return <RequireUserRole role={Role.MEMBER} {...props} />;
}

interface CapabilityGuardProps {
  FallbackComponent?: JSX.Element;
  children: React.ReactNode;
}

// SEE-946: gates routes/sections accessible to Primary Admin OR Secondary Admin.
// Used for settings, cards, spend rules, user management, etc. Anywhere
// `RequireAdministrator` was previously used purely for "admin-only platform
// management" and the matrix grants Secondary access too.
export function RequireAdminOrSecondary({ FallbackComponent, children }: CapabilityGuardProps): JSX.Element {
  const { canManageUsersAndCards } = useUserRole();
  if (canManageUsersAndCards) {
    return <>{children}</>;
  }
  if (FallbackComponent) {
    return FallbackComponent;
  }
  return <></>;
}

// SEE-946: gates routes/sections that show transactions and reviews.
// Allows Primary Admin, Group Administrator, and Approval Admin.
// Excludes Secondary Admin (no transaction visibility) and Cardholder (own only).
export function RequireTransactionsAccess({
  FallbackComponent,
  children,
}: CapabilityGuardProps): JSX.Element {
  const { canAccessTransactions } = useUserRole();
  if (canAccessTransactions) {
    return <>{children}</>;
  }
  if (FallbackComponent) {
    return FallbackComponent;
  }
  return <></>;
}

/**
 * SEE-946: generic URL-level route guard.
 *
 * The pre-SEE-946 codebase mostly relied on hiding tabs from the navigation
 * menu to gate per-role access — fine for nav clicks, but a user pasting
 * `/cards/spend-rules` into the URL bar would render the gated page on roles
 * that lack the surface. With Secondary admins having no Overview at all and
 * Approval admins having a narrower surface than Primary, that gap becomes
 * meaningfully exploitable.
 *
 * Usage: wrap any Route element with a predicate that reads the
 * `useUserRole()` composite. On block, redirects to the user's role-specific
 * landing page (centralised in app/auth/roleAccess.ts) instead of rendering
 * something they shouldn't see. Preserves the loading state so we don't flash
 * a redirect while userCompanies is still resolving.
 *
 * Why a callback rather than a string capability key:
 *   The composites in useUserRole are typed booleans on its return value.
 *   A callback like `(role) => role.canAccessTransactions` is exhaustively
 *   type-checked, refactor-safe, and lets callers AND/OR composites at the
 *   call site without growing the hook's surface area.
 */
type UserRoleComposites = ReturnType<typeof useUserRole>;
export interface RoleRouteGuardProps {
  /**
   * Predicate over the useUserRole() return value. Return true to render the
   * children, false to redirect.
   */
  canAccess: (composites: UserRoleComposites) => boolean;
  /**
   * Override the default redirect target. Defaults to the user's
   * role-specific landing (roleAccess.defaultLanding).
   */
  redirectTo?: string;
  children: React.ReactNode;
}

export function RoleRouteGuard({ canAccess, redirectTo, children }: RoleRouteGuardProps): JSX.Element {
  const composites = useUserRole();
  const { userRole, isUserCompaniesStateUnknown, selectedPaidolId } = useUserCompanies();

  // Don't redirect while the role is still loading — that flashes the user to
  // the Cardholder default landing during the initial Redux hydration and
  // then has to bounce them back. Render nothing instead; RequireAuth's
  // top-level LoadingOverlay covers the visual. Missing userRole counts as
  // loading only until a company is selected: with a selection in place, no
  // role is a RESOLVED state (a non-support user with no membership row fails
  // closed) and must fall through to the redirect, not a permanent blank page.
  if (isUserCompaniesStateUnknown || (!userRole && !selectedPaidolId)) {
    return <></>;
  }

  if (canAccess(composites)) {
    return <>{children}</>;
  }

  return <Navigate to={redirectTo ?? composites.defaultLandingRoute} replace />;
}

/**
 * SEE-265: Speedchain-staff-only routes (Control Center). Gates on the User row's
 * is_support, not the per-company role — support users are often not members of
 * the company they have selected. This only hides the page; any data behind it
 * must re-check is_support server-side.
 */
export function RequireSupport({ children }: { children: React.ReactNode }): JSX.Element {
  const { user, isUserCompaniesStateUnknown } = useUserCompanies();

  // Same as RoleRouteGuard: render nothing while the user row is still loading
  // rather than bouncing a support user off the page during hydration.
  if (isUserCompaniesStateUnknown || user === undefined) {
    return <></>;
  }

  if (user?.is_support) {
    return <>{children}</>;
  }

  return <Navigate to="/landing" replace />;
}

export default RequireUserRole;
